import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactHlsPlayer from 'react-hls-player';
import { BroadcastStream, StreamType } from 'videoservice-openapi-react';
import { useGetStream } from 'features/broadcast/get-stream';
import { EDialogSize, useDialogContext } from 'shared/lib/dialog';
import { TwitchPlayer, VideoIcon } from 'shared/ui';
import { LoadingIndicator } from 'shared/ui/loading-indicator';
import { StreamCellStyled, StreamIFrameStyled } from './stream-cell.styled';
import { IStreamCellProps } from './stream-cell.types';

export const StreamCell: FC<IStreamCellProps> = ({ broadcast }) => {
  const [stream, setStream] = useState<BroadcastStream | undefined>();
  const { getStream, isLoading, isFetching } = useGetStream({
    broadcastId: broadcast.id,
    redirectUrl: window.location.origin,
    refererUrl: window.location.origin,
    streamType: broadcast.streamType ?? StreamType.HLS,
  });
  const { openDialog } = useDialogContext();
  const playerRef = useRef<HTMLVideoElement>(null);

  const streamClickHandler = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();

      if (isLoading || isFetching || !broadcast.live) {
        return;
      }

      const streamData = await getStream();
      setStream(streamData);
    },
    [broadcast.live, getStream, isLoading, isFetching],
  );

  useEffect(() => {
    if (stream) {
      try {
        openDialog({
          closable: true,
          size: EDialogSize.L,
          content:
            stream.type === StreamType.IFRAME ? (
              broadcast.streamUrl?.includes('twitch.tv') ? (
                <TwitchPlayer
                  url={broadcast.streamUrl}
                  width={640}
                  height={480}
                />
              ) : (
                <StreamIFrameStyled src={stream.url} />
              )
            ) : (
              <ReactHlsPlayer
                playerRef={playerRef}
                src={stream.url}
                autoPlay={true}
                controls={true}
                width="640"
                height="480"
              />
            ),
        });
      } catch (error) {
        setStream(undefined);
      }
    }
  }, [broadcast.streamUrl, openDialog, stream]);

  return (
    <StreamCellStyled $isLive={broadcast.live}>
      <div className="icon" onClick={streamClickHandler}>
        {(isLoading || isFetching) && <LoadingIndicator />}
        {!isLoading && !isFetching && <VideoIcon />}
      </div>
      <div className="stream-info">{broadcast.title}</div>
    </StreamCellStyled>
  );
};
